import React from 'react';
import { Button, Input, InputOnChangeData, MessageBar, Spinner } from '@fluentui/react-components';
import {
    useGetPendingLoginQuery, useStorefrontLoginMutation, useStorefrontLoginWithCodeMutation,
} from '../../Api/Api';
import { LoginMethod } from '../../Api/Models/StorefrontLogin';

// The storefront sign-in state machine, shared by the header popover and the full login page so the
// two can't drift. Three shapes depending on the storefront's loginMethod:
//
//   magicLink — ask for the email, then say "check your email". Today's behavior, unchanged.
//   code      — ask for the email, then for the code that arrives, and sign in without navigating.
//   poll      — ask for the email, then wait while the shopper opens the link in another tab.
//
// The signed-in step is the same for all three: hand the token up and let the host decide what to do
// with it (the popover reloads in place, the login page navigates on).

type Step = 'email' | 'sent' | 'code' | 'waiting';

// Polls often enough to feel immediate without hammering the BFF; the pending record lives 10 minutes
// and the server does the real expiry, so this only has to stop being wasteful.
const POLL_MS = 2500;

interface IStorefrontSignInProps {
    organization: string;
    storefront: string;
    /** The storefront's configured method. Absent/unknown behaves as magicLink. */
    method?: string;
    /** Where to send the shopper after signing in; passed through to the emailed link. */
    redirect?: string;
    /** Called with the session token once the sign-in completes. */
    onSignedIn: (token: string) => void;
    /** Rendered above the form, e.g. the "your session expired" notice. */
    message?: string;
    allowSignup?: boolean;
    /**
     * Carries the address already typed, so the sign-up page can pre-fill it. The host owns the
     * navigation; this component owns the input, so the value has to travel with the call.
     */
    onSignup?: (email?: string) => void;
    /**
     * The host's localization resolver. Passed in rather than resolved here because the two hosts sit
     * on opposite sides of the auth boundary: useLocalization() reads the token-gated settings query
     * and would throw on the unauthenticated login page, which has to use the basic-settings variant.
     */
    L: (key: string, fallback?: string) => string;
    /**
     * Styling for the controls, supplied by whichever surface is hosting them. The login PAGE passes
     * its loginPageConfig parts; the header popover passes nothing and keeps the header's own account
     * styling — the two live under different config, so neither imposes its look on the other.
     */
    inputStyle?: React.CSSProperties;
    /**
     * Colour/family for the field's INNER input. Separate from inputStyle because Fluent keeps
     * `style` on the Input's root span and paints the editable element from its own tokens, so
     * without this the shopper types dark theme-default text into a dark configured field.
     */
    inputTextStyle?: React.CSSProperties;
    primaryButtonStyle?: React.CSSProperties;
    secondaryButtonStyle?: React.CSSProperties;
    messageStyle?: React.CSSProperties;
}

const StorefrontSignIn: React.FC<IStorefrontSignInProps> = (props) => {
    const { L } = props;
    const [login] = useStorefrontLoginMutation();
    const [submitCode] = useStorefrontLoginWithCodeMutation();

    const [step, setStep] = React.useState<Step>('email');
    const [busy, setBusy] = React.useState(false);
    const [emailAddress, setEmailAddress] = React.useState('');
    const [code, setCode] = React.useState('');
    const [pendingId, setPendingId] = React.useState<string | undefined>();
    const [notice, setNotice] = React.useState<string | undefined>();
    const [error, setError] = React.useState<string | undefined>();

    const method = (props.method ?? 'magicLink') as LoginMethod;

    // Only polls while actually waiting; RTK Query stops the interval as soon as `skip` flips back.
    const poll = useGetPendingLoginQuery(
        { organization: props.organization, storefront: props.storefront, pendingId: pendingId ?? '' },
        { skip: step !== 'waiting' || !pendingId, pollingInterval: POLL_MS },
    );

    React.useEffect(() => {
        if (step !== 'waiting' || !poll.data) return;
        if (poll.data.status === 'confirmed' && poll.data.token) {
            props.onSignedIn(poll.data.token);
        } else if (poll.data.status === 'expired') {
            setStep('email');
            setPendingId(undefined);
            setError(L('localization_auth_signin_expired', 'This sign-in request expired. Please try again.'));
        }
    }, [poll.data, step, props, L]);

    const onRequestEmail = async () => {
        if (!emailAddress) return;
        setBusy(true);
        setError(undefined);
        const result = await login({
            organization: props.organization,
            storefront: props.storefront,
            emailAddress: emailAddress.toLowerCase(),
            redirect: props.redirect ?? '',
        });
        setBusy(false);
        const data = result.data;
        setNotice(data?.message);
        // The server decides the method, not the client: it knows whether the pending record was
        // actually created. Falling back to the plain "check your email" step is always safe.
        if (data?.pendingId && data.method === 'code') {
            setPendingId(data.pendingId);
            setStep('code');
        } else if (data?.pendingId && data.method === 'poll') {
            setPendingId(data.pendingId);
            setStep('waiting');
        } else {
            setStep('sent');
        }
    };

    const onSubmitCode = async () => {
        if (!pendingId || !code) return;
        setBusy(true);
        setError(undefined);
        const result = await submitCode({
            organization: props.organization, storefront: props.storefront, pendingId, code: code.trim(),
        });
        setBusy(false);
        const data = result.data;
        if (data?.status === 'confirmed' && data.token) {
            props.onSignedIn(data.token);
            return;
        }
        setCode('');
        if (data?.status === 'expired') {
            // The pending is gone — a fresh email is the only way forward, so go back to the start
            // rather than leaving the shopper typing into a dead field.
            setStep('email');
            setPendingId(undefined);
        }
        setError(data?.error ?? L('localization_auth_code_invalid', "That code isn't right."));
    };

    const restart = () => {
        setStep('email');
        setPendingId(undefined);
        setCode('');
        setError(undefined);
        setNotice(undefined);
    };

    if (busy) return <Spinner label={L('localization_auth_logging_in')} />;

    return (
        <div>
            {props.message && <MessageBar style={{ marginBottom: '1em', ...props.messageStyle }}>{props.message}</MessageBar>}
            {error && <MessageBar intent='error' style={{ marginBottom: '1em', ...props.messageStyle }}>{error}</MessageBar>}

            {step === 'email' && (
                <form onSubmit={(e) => { e.preventDefault(); onRequestEmail(); }}>
                    <Input
                        placeholder={L('localization_auth_email_address')}
                        value={emailAddress}
                        onChange={(_: React.ChangeEvent<HTMLInputElement>, d: InputOnChangeData) => setEmailAddress(d.value)}
                        style={{ width: '100%', ...props.inputStyle }}
                        input={props.inputTextStyle ? { style: props.inputTextStyle } : undefined}
                    />
                    <Button appearance='primary' type='submit' style={{ width: '100%', marginTop: '1em', ...props.primaryButtonStyle }}>
                        {L('localization_auth_login_button')}
                    </Button>
                    {props.allowSignup && props.onSignup && (
                        <Button appearance='subtle' style={{ width: '100%', marginTop: '1em', ...props.secondaryButtonStyle }} onClick={() => props.onSignup?.(emailAddress)}>
                            {L('localization_auth_signup_button')}
                        </Button>
                    )}
                </form>
            )}

            {step === 'sent' && <div>{notice}</div>}

            {step === 'code' && (
                <form onSubmit={(e) => { e.preventDefault(); onSubmitCode(); }}>
                    <p style={{ marginTop: 0 }}>
                        {L('localization_auth_code_prompt', 'Enter the code we emailed you.')}
                    </p>
                    <Input
                        // inputMode/autoComplete let phones show a number pad and offer the code from
                        // the SMS/email autofill, which is most of the benefit of a code over a link.
                        placeholder={L('localization_auth_code_placeholder', '000000')}
                        value={code}
                        inputMode='numeric'
                        autoComplete='one-time-code'
                        maxLength={6}
                        onChange={(_: React.ChangeEvent<HTMLInputElement>, d: InputOnChangeData) => setCode(d.value.replace(/\D/g, ''))}
                        style={{ width: '100%', ...props.inputStyle }}
                        input={props.inputTextStyle ? { style: props.inputTextStyle } : undefined}
                    />
                    {/* The configured look is dropped while disabled: an inline background beats
                        Fluent's disabled styling, so a styled button would look live but do nothing. */}
                    <Button appearance='primary' type='submit' style={{ width: '100%', marginTop: '1em', ...(code.length < 6 ? undefined : props.primaryButtonStyle) }} disabled={code.length < 6}>
                        {L('localization_auth_code_submit', 'Sign in')}
                    </Button>
                    <Button appearance='subtle' style={{ width: '100%', marginTop: '1em', ...props.secondaryButtonStyle }} onClick={restart}>
                        {L('localization_auth_code_restart', 'Use a different email')}
                    </Button>
                </form>
            )}

            {step === 'waiting' && (
                <div>
                    <Spinner label={L('localization_auth_waiting_for_link', 'Waiting for you to open the link we emailed you...')} />
                    <p style={{ fontSize: 12, color: '#555' }}>
                        {L('localization_auth_waiting_hint', 'Open it in this browser and you will be signed in here automatically.')}
                    </p>
                    <Button appearance='subtle' style={{ width: '100%', marginTop: '1em', ...props.secondaryButtonStyle }} onClick={restart}>
                        {L('localization_auth_code_restart', 'Use a different email')}
                    </Button>
                </div>
            )}
        </div>
    );
};

export default StorefrontSignIn;
